/**
 * Default statistics object for session stats.
 *
 * @typedef {Object} Stats
 * @property {Object} quickNameReview - Statistics for quick name review.
 * @property {number} quickNameReview.prev - Previous quick name review count.
 * @property {number} quickNameReview.cur - Current quick name review count.
 * @property {Object} fullNameReview - Statistics for full name review.
 * @property {number} fullNameReview.prev - Previous full name review count.
 * @property {number} fullNameReview.cur - Current full name review count.
 * @property {Object} familyReview - Statistics for family review.
 * @property {number} familyReview.prev - Previous family review count.
 * @property {number} familyReview.cur - Current family review count.
 * @property {Object} indexing - Statistics for indexing.
 * @property {Object} indexing.indexed - Indexed statistics.
 * @property {number} indexing.indexed.prev - Previous indexed count.
 * @property {number} indexing.indexed.cur - Current indexed count.
 * @property {Object} indexing.reviewed - Reviewed statistics.
 * @property {number} indexing.reviewed.prev - Previous reviewed count.
 * @property {number} indexing.reviewed.cur - Current reviewed count.
 */
const defaultStats = {
  quickNameReview: { prev: 0, cur: 0 },
  fullNameReview: { prev: 0, cur: 0 },
  familyReview: { prev: 0, cur: 0 },
  indexing: {
    indexed: { prev: 0, cur: 0 },
    reviewed: { prev: 0, cur: 0 },
  },
}

/**
 * @constant {string} key - The key used for user session statistics.
 */
const key = 'give:user-session-stats'

/**
 * Parses a JSON string and returns the corresponding object.
 * If parsing fails, returns a default stats object.
 *
 * @param {string} str - The JSON string to parse.
 * @returns {Object} - The parsed object or the default stats object if parsing fails.
 */
const returnStats = (str) => {
  try {
    return JSON.parse(str)
  } catch (e) {
    return defaultStats
  }
}

/**
 * Updates the session statistics for a given task type.
 *
 * @param {string} taskType - The type of task being performed. Must be one of 'Name Correction', 'Full Name Review', or 'Family Review'.
 * @throws {Error} Throws an error if the task type is invalid.
 */
const updateSessionStats = async (taskType) => {
  if (taskType !== 'Name Correction' && taskType !== 'Full Name Review' && taskType !== 'Family Review') {
    throw new Error(`Invalid task type: ${taskType}`)
  }
  const type =
    // eslint-disable-next-line no-nested-ternary
    taskType === 'Name Correction'
      ? 'quickNameReview'
      : taskType === 'Full Name Review'
        ? 'fullNameReview'
        : 'familyReview'
  const stats = returnStats(sessionStorage.getItem(key))
  const currentStats = { ...defaultStats, ...stats }
  const updatedStats = { ...currentStats, [type]: { prev: currentStats[type].prev, cur: currentStats[type].cur + 1 } }
  sessionStorage.setItem(key, JSON.stringify(updatedStats))
}

export default updateSessionStats
